import React from 'react';
import PropTypes from 'prop-types';

// MUI components
import {Typography, Button} from '@mui/material';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import {useMediaQuery, useTheme} from '@mui/material';
import Alert from '@mui/material/Alert';

// Styled
import {StyledAccordion, StyledBadge} from './styled';

import BasicTable from 'components/basic-table';

import {ACCORDION_TABLE_COMPONENT_TEST_ID} from 'testidConstants.js';

const AccordionTable = ({
  title,
  accordionHeader,
  tableData,
  fieldNames,
  expanded,
  children,
  getTableData,
  loadMore,
  shouldShowLoadButton,
  isLoading,
  isError,
  showBadge,
  badgeCount,
  globalRestriction,
  resourceId
}) => {
  const mobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const tabletStart = useMediaQuery('(min-width:900px)');
  const tabletFinish = useMediaQuery('(max-width:959px)');
  const tablet = tabletStart && tabletFinish;

  const getOnChangeHandler = () => {
    const dataShoulBeFetched = !tableData && getTableData;
    return dataShoulBeFetched ? getTableData : null;
  };

  const getOnClickHandler = () => {
    if (!tableData) {
      getTableData();
    } else {
      loadMore();
    }
  };

  return (
    <>
      <Typography variant="overline">{title}</Typography>
      <StyledAccordion
        data-testid={ACCORDION_TABLE_COMPONENT_TEST_ID}
        defaultExpanded={expanded}
        onChange={getOnChangeHandler()}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body1">
            {showBadge ? (
              <StyledBadge badgeContent={badgeCount} color="primary" max={1000000} showZero>
                {accordionHeader}
              </StyledBadge>
            ) : (
              accordionHeader
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          style={{
            overflowWrap: 'anywhere',
            display: 'flex',
            flexDirection: 'column',
            overflowX: mobile && 'scroll',
            overflowY: 'hidden',
            maxHeight: tablet && '2400px'
          }}
        >
          {(!tableData || tableData.length < 1) && isLoading ? (
            <CircularProgress
              size={24}
              style={{marginLeft: 16, visibility: isLoading ? 'visible' : 'hidden'}}
            ></CircularProgress>
          ) : (
            <>
              {children}

              {tableData?.length > 0 && (
                <BasicTable
                  tableData={tableData}
                  fieldNames={fieldNames}
                  globalRestriction={globalRestriction}
                  resourceId={resourceId}
                />
              )}
              {isError && (
                <Alert severity="error">
                  There are problems with fetching {accordionHeader} resources. Please try again.
                </Alert>
              )}
              {shouldShowLoadButton && (
                <Button style={{marginTop: 16}} disabled={isLoading} onClick={() => getOnClickHandler()} size="small">
                  {isError ? 'Retry' : 'Show More'}
                  <CircularProgress
                    size={24}
                    style={{marginLeft: 16, visibility: isLoading ? 'visible' : 'hidden'}}
                  ></CircularProgress>
                </Button>
              )}
            </>
          )}
        </AccordionDetails>
      </StyledAccordion>
    </>
  );
};

AccordionTable.propTypes = {
  title: PropTypes.string,
  accordionHeader: PropTypes.string,
  tableData: PropTypes.array,
  fieldNames: PropTypes.array,
  expanded: PropTypes.bool,
  getTableData: PropTypes.func,
  children: PropTypes.object,
  loadMore: PropTypes.func,
  shouldShowLoadButton: PropTypes.bool,
  isLoading: PropTypes.bool,
  showBadge: PropTypes.bool,
  isError: PropTypes.bool,
  badgeCount: PropTypes.number,
  globalRestriction: PropTypes.object,
  resourceId: PropTypes.string
};
export default AccordionTable;
